import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import InformationOverlay from '../../../components/InformationOverlay';
import DiscountSummaryList from '../../../components/DiscountSummaryList';
import CouponOverlay from '../CouponOverlay';

import './style.scss';

const CartSummaryV2 = ({ showProducts }) => {
  const { t } = useTranslation();
  const commerce = useSelector((state) => state.get('commerce'));

  const totalWithTax = commerce.getIn([
    'cart',
    'content',
    'attributes',
    'total_price',
    'formatted',
  ]);
  const totalPrice = parseFloat(
    commerce.getIn(
      ['cart', 'content', 'attributes', 'total_price', 'number'],
      0,
    ),
  );
  const isFreeProduct = totalPrice === 0;

  const rawAdjustments = commerce.getIn(
    ['cart', 'content', 'attributes', 'order_total', 'adjustments'],
    Immutable.List(),
  );

  // Merge multiple promotion adjustments into a single row
  const promotionAdjustments = rawAdjustments.filter(
    (adj) => adj.get('type') === 'promotion',
  );
  const hasPromotion = promotionAdjustments.size > 0;

  // The discount overlay differs by source: a coupon-driven promotion shows the
  // coupon detail; a volume discount (staffelkorting) shows the tier breakdown.
  // Drive this off the is_coupon flag on the order-item adjustments (the
  // order_total adjustments don't carry it), not the quantity — a volume
  // discount can apply at any quantity.
  const showApplyCouponField = commerce
    .getIn(['cart', 'products'], Immutable.List())
    .some((product) => product
      .getIn(['attributes', 'adjustments'], Immutable.List())
      .some((adj) => adj.get('type') === 'promotion' && adj.get('is_coupon')),
    );
  const promotionTotalNumber = promotionAdjustments.reduce(
    (sum, adj) => sum + parseFloat(adj.getIn(['total', 'number'], 0)),
    0,
  );
  const promotionFormatted = hasPromotion
    ? `€ ${promotionTotalNumber.toFixed(2).replace('.', ',')}`
    : null;
  const nonPromotionAdjustments = rawAdjustments.filter(
    (adj) => adj.get('type') !== 'promotion',
  );

  return (
    <>
      {showProducts &&
        commerce
          .getIn(['cart', 'products'], Immutable.List())
          .map((product) => {
            const cartQuantity = parseInt(
              product.getIn(['attributes', 'quantity'], 1),
            );
            const singleProduct = cartQuantity === 1;
            const productLabel = singleProduct
              ? t('checkout.single_seat')
              : t('checkout.multiple_seats');

            const productTotalPrice = parseFloat(
              product.getIn(['attributes', 'total_price', 'number'], 0),
            );
            const priceDisplay =
              productTotalPrice === 0
                ? t('cart.free')
                : product.getIn(['attributes', 'total_price', 'formatted']);

            return (
              <div key={product.get('id')} className="CartPopup__product">
                <div className="CartPopup__product__titlerow">
                  <div className="CartPopup__product__seats">
                    {`${cartQuantity} ${productLabel}`}
                    <InformationOverlay
                      overlayKey="CartSummary__seats"
                      title={t('checkout.seats_overlay_title')}
                      body={t('checkout.seats_overlay_body')}
                    />
                  </div>
                  <div>{priceDisplay}</div>
                </div>
              </div>
            );
          })}

      {hasPromotion && (
        <div className="CartSummary__product__titlerow">
          <div className="CartPopup__product__seats">
            {t('cart.discount')}
            {showApplyCouponField ? (
              <CouponOverlay
                products={commerce.getIn(
                  ['cart', 'products'],
                  Immutable.List(),
                )}
              />
            ) : (
              <InformationOverlay
                overlayKey="CartSummary__discount"
                title={t('cart.discount')}
              >
                <DiscountSummaryList
                  products={commerce.getIn(
                    ['cart', 'products'],
                    Immutable.List(),
                  )}
                />
              </InformationOverlay>
            )}
          </div>
          <div>{promotionFormatted}</div>
        </div>
      )}

      {nonPromotionAdjustments.map((adjustment) => {
        switch (adjustment.get('type')) {
          case 'shipping':
            return (
              <div
                key={adjustment.get('type')}
                className="CartSummary__product__titlerow"
              >
                <div>{t('cart.shippingCost')}</div>
                <div>{adjustment.getIn(['total', 'formatted'])}</div>
              </div>
            );
          case 'tax':
            if (!isFreeProduct) {
              return (
                <div
                  key={adjustment.get('type')}
                  className="CartSummary__product__titlerow"
                >
                  <div>{t('cart.VAT')}</div>
                  <div>{adjustment.getIn(['total', 'formatted'])}</div>
                </div>
              );
            }
            break;
          default:
            break;
        }
      })}

      {!isFreeProduct && <hr />}

      <div className="CartSummary__product__titlerow">
        <div>
          <strong>
            {!isFreeProduct ? t('cart.totalWithVAT') : t('cart.total')}
          </strong>
        </div>
        <div>
          <strong>{totalWithTax}</strong>
        </div>
      </div>
    </>
  );
};

export default CartSummaryV2;
